import React from 'react';
import styled, { css } from 'styled-components';

type BaseVariants = {
  readonly h1?: undefined;
  readonly h2?: undefined;
  readonly h3?: undefined;
  readonly h4?: undefined;
  readonly h5?: undefined;
  readonly h6?: undefined;
  readonly p?: undefined;
  readonly body1?: undefined;
  readonly body2?: undefined;
  readonly link?: undefined;
  readonly error?: undefined;

  readonly bold?: undefined;
  readonly color?: undefined;
  readonly underline?: undefined;
};

export type TextColor =
  | '#1B1A1F'
  | '#239AE7'
  | '#DD6C6C'
  | '#FFFFFF'
  | '#8D8D8D'
  | '#6A0E00'
  | '#DB0000';

type Props = {
  readonly children: React.ReactNode;
} & (
  | (Omit<BaseVariants, 'h1'> & { readonly h1?: true })
  | (Omit<BaseVariants, 'h2'> & { readonly h2?: true })
  | (Omit<BaseVariants, 'h3'> & { readonly h3?: true })
  | (Omit<BaseVariants, 'h4'> & { readonly h4?: true })
  | (Omit<BaseVariants, 'h5'> & { readonly h5?: true })
  | (Omit<BaseVariants, 'h6'> & { readonly h6?: true })
  | (Omit<BaseVariants, 'p' | 'bold' | 'underline' | 'color'> & {
      readonly p?: true;
      readonly bold?: true;
      readonly underline?: true;
      readonly color?: TextColor;
    })
  | (Omit<BaseVariants, 'body1' | 'bold' | 'underline' | 'color'> & {
      readonly body1?: true;
      readonly bold?: true;
      readonly underline?: true;
      readonly color?: TextColor;
    })
  | (Omit<BaseVariants, 'body2' | 'bold' | 'underline' | 'color'> & {
      readonly body2?: true;
      readonly bold?: true;
      readonly underline?: true;
      readonly color?: TextColor;
    })
  | (Omit<BaseVariants, 'link'> & { readonly link?: true })
  | (Omit<BaseVariants, 'error'> & { readonly error?: true })
);

const StyledWrapper = styled.span.attrs(
  ({ color, h1, bold, underline }: Props) => ({
    style: {
      textDecoration: underline ? 'underline' : undefined,
      color,
      // eslint-disable-next-line no-nested-ternary
      fontWeight: bold && h1 ? 700 : bold ? 600 : undefined,
    },
  }),
)<Omit<Props, 'children'>>`
  ${({ h1, h2, h3, h4, h5, h6, p, body1, body2, link, error }) => {
    if (h1) {
      return css`
        font-size: 32px;
        font-weight: 700;
      `;
    }
    if (h2) {
      return css`
        font-size: 24px;
        font-weight: 700;
      `;
    }
    if (h3) {
      return css`
        font-size: 20px;
        font-weight: 600;
      `;
    }
    if (h4) {
      return css`
        font-size: 16px;
        font-weight: 600;
      `;
    }
    if (h5) {
      return css`
        font-size: 18px;
        font-weight: 400;
      `;
    }
    if (h6) {
      return css`
        font-size: 10px;
        font-weight: 600;
      `;
    }
    if (p) {
      return css`
        font-size: 16px;
        font-weight: 400;
      `;
    }
    if (body1) {
      return css`
        font-size: 12px;
        font-weight: 400;
      `;
    }
    if (body2) {
      return css`
        font-size: 12px;
        font-weight: 300;
      `;
    }
    if (error) {
      return css`
        font-size: 14px;
        color: ${({ theme }) => theme.color('#DD6C6C')};
      `;
    }
    if (link) {
      return css`
        font-size: 14px;
        font-weight: 600;
        text-decoration: underline;
      `;
    }

    return undefined;
  }}
`;

export const T = ({ children, ...rest }: Props) => (
  <StyledWrapper {...rest}>{children}</StyledWrapper>
);
